import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Chart from 'react-apexcharts';
import _ from 'lodash';
import { NavLink } from 'react-router-dom';
import classes from './topPhysiciansByRevenue.module.scss';
import Button from '../../../_common/Form/Buttons/Button';
import InputField from '../../../_common/Form/TextFields/InputField';
import Select from '../../../_common/Form/Selects/Select/Select';
import { getSelectRepListRequest } from '../../../../store/actions/users';
import { useQuery } from '../../../../utils/hooks/useQuery';
import MonthPicker from '../../../_common/Form/DatePickers/MonthPicker/MonthPicker';
import Api from '../../../../api/api';
import Loader from '../../../_common/Loader/Loader';
import FilterButton from '../../../_common/FilterButton/FilterButton';
import accountHelpers from '../../../../utils/accountHelpers';

const chartTemplate = {
  options: {
    chart: {
      height: 350,
      type: 'bar',
      toolbar: {
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
    },

    legend: {
      show: false,
    },

    dataLabels: {
      enabled: false,
    },

    colors: [
      '#405DF9',
      '#FF0000',
      '#00C974',
      '#F76E11',
      '#FFD700',
      '#3AACFF',
      '#855CF8',
      '#FFA338',
      '#00CED1',
      '#E905E0',
    ],

    fill: {
      opacity: 1,
    },

    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '90%',
      },
    },

    grid: {
      show: true,
      strokeDashArray: 2,
    },

    tooltip: {
      style: {
        fontSize: '12px',
        fontFamily: 'Inter, sans-serif',
      },
      y: {
        formatter(val) {
          const formatter = Intl.NumberFormat('en');
          return formatter.format(val);
        },
      },
    },

    xaxis: {
      categories: [],
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      crosshairs: {
        fill: {
          type: 'gradient',
          gradient: {
            colorFrom: '#D8E3F0',
            colorTo: '#BED1E6',
            stops: [0, 100],
            opacityFrom: 0.4,
            opacityTo: 0.5,
          },
        },
      },
      tooltip: {
        enabled: false,
      },
    },

    yaxis: {
      labels: {
        style: {
          colors: ['rgba(29, 39, 64, 0.5)'],
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          cssClass: 'apexcharts-yaxis-label',
        },
        // show: false,
        formatter(val) {
          const formatter = Intl.NumberFormat('en', { notation: 'compact' });
          return formatter.format(val);
        },
      },
    },
  },

  series: [{
    name: '',
    data: [],
  }],
};

const thisMonth = moment().format('yyyy-MM');
const limit = 20;

const TopPhysiciansByRevenue = () => {
  const { query, queryToStringify, setQuery } = useQuery();
  const isManufacturerUsers = accountHelpers.isInRole('Manufacturer Users');

  const dispatch = useDispatch();
  const { selectRepList, pageCount, currentPage } = useSelector((store) => store.users);

  const firstLoad = useRef(true);
  const timeout = useRef(null);

  const [chart, setChart] = useState(chartTemplate);

  const [physicians, setPhysicians] = useState([]);
  const [searchValue, setSearchValue] = useState('');

  const [filters, setFilters] = useState({
    start: query.start ? moment(query.start).format('yyyy-MM') : thisMonth,
    end: query.end && moment(query.start).format('yyyy-MM') !== moment(query.end).format('yyyy-MM')
      ? moment(query.end).format('yyyy-MM') : '',
    rep: query.rep || '',
  });

  const [chartType, setChartType] = useState(query.chartType || 'revenue');

  const [loading, loadingToggle] = useState(true);
  const [chartLoading, chartLoadingToggle] = useState(false);

  const getChart = async (sendingData) => {
    const { data: { data: physiciansChart } } = await Api.getTopPhysiciansByRevenue(sendingData);

    const newChart = _.cloneDeep(chartTemplate);

    newChart.options.xaxis.categories = sendingData.months.map((m) => moment(m, 'yyyy-MM').format('MMM yyyy'));
    newChart.data = physiciansChart;
    newChart.series = physiciansChart.map((p) => ({
      name: p.name,
      data: _.compact([p?.[filters.start]?.[chartType], p?.[filters.end]?.[chartType]]),
    }));

    await setChart(newChart);

    return physiciansChart;
  };

  useEffect(() => {
    (async () => {
      loadingToggle(true);
      setQuery({ ...filters, chartType });

      const sendingData = { rep: filters.rep };
      sendingData.months = _.compact([filters.start, filters.end]);

      const requests = [getChart({ ...sendingData })];

      if (firstLoad.current) {
        requests.push(dispatch(getSelectRepListRequest({ limit, role: 'Rep' })));

        firstLoad.current = false;
      }

      const [physiciansData] = await Promise.all(requests);

      const { data: { data: physiciansList } } = await Api.getPhysiciansByRevenue(sendingData);

      setPhysicians(_.uniqBy([...physiciansData.map(({ id, name }) => ({ id, name })), ...physiciansList], 'id')
        .map((l, i) => ({
          ...l,
          fullName: l.name || `${l.firstName} ${l.lastName}`,
          active: i < 10,
          color: chartTemplate.options.colors[i] || '#949292c4',
        })));

      loadingToggle(false);
    })();
  }, [filters]);

  const nextOptionListRequest = async (page) => {
    await dispatch(getSelectRepListRequest({ page, limit, role: 'Rep' }));
  };

  const changeFilterData = (path, value) => {
    setFilters((prev) => ({ ...prev, [path]: value }));
  };

  const changePhysiciansData = async (id, check) => {
    clearTimeout(timeout.current);
    let i = 0;

    const newPhysicians = physicians.map((p) => (p.id === id ? { ...p, active: check } : p))
      .map((p) => {
        const newData = { ...p, color: '#949292c4' };

        if (p.active) {
          newData.color = chartTemplate.options.colors[i];
          i++;
        }

        return newData;
      });
    setPhysicians(newPhysicians);

    chartLoadingToggle(true);

    const sendingData = { rep: filters.rep };
    sendingData.months = _.compact([filters.start, filters.end]);

    timeout.current = setTimeout(async () => {
      await getChart({
        ...sendingData,
        ids: newPhysicians.filter((p) => p.active).map((p) => p.id),
      });

      chartLoadingToggle(false);
    }, 500);
  };

  const changeType = async (type) => {
    chartLoadingToggle(true);

    const newChart = _.cloneDeep(chart);

    newChart.series = newChart.data.map((d) => ({
      ...d,
      data: _.compact([d?.[filters.start]?.[type], d?.[filters.end]?.[type]]),
    }));

    await setChart(newChart);
    setChartType(type);
    setQuery({ ...filters, chartType: type });
    chartLoadingToggle(false);
  };

  const clearFilters = () => {
    setFilters({
      start: thisMonth,
      end: '',
      rep: '',
    });

    setSearchValue('');
  };

  const { disabled, activePhysiciansCount } = useMemo(() => {
    const physiciansLength = physicians.filter((p) => p.active).length;

    return { disabled: physiciansLength === 10, activePhysiciansCount: physiciansLength };
  }, [physicians]);

  const filteredPhysicians = useMemo(() => (
    physicians.filter((p) => p.fullName.toLowerCase().includes(searchValue.toLowerCase()))
  ), [physicians, searchValue]);

  const chartTypes = useMemo(() => {
    const types = [
      {
        title: 'Total Sales',
        key: 'revenue',
        value: 'revenue',
        width: 90,
      },
      {
        title: 'Profit',
        key: 'profit',
        value: 'profit',
        width: 90,
      },
      {
        title: 'Cost',
        key: 'cost',
        value: 'cost',
        width: 90,
      },
    ];

    if (isManufacturerUsers) {
      return types.filter((t) => t.key !== 'profit');
    }

    return types;
  }, [isManufacturerUsers]);

  return (
    <div className={classes.wrapper}>
      <div className={classes.chart_wrapper}>
        <div className={classes.filters_wrapper}>
          <div className={classes.picker_wrapper}>
            <MonthPicker
              date={filters.start}
              excludeDate={filters.end}
              hideClearButton={!filters.end}
              size="small"
              onChange={(m) => changeFilterData('start', m)}
            />

            <MonthPicker
              date={filters.end}
              excludeDate={filters.start}
              hideClearButton={!filters.start}
              size="small"
              onChange={(m) => changeFilterData('end', m)}
            />
          </div>

          <div className={classes.select_wrapper}>
            <Select
              label="All reps"
              nextOptionListRequest={nextOptionListRequest}
              options={selectRepList}
              totalPage={pageCount}
              currentPage={currentPage}
              limit={limit}
              currentOption={filters.rep}
              onChange={(option) => changeFilterData('rep', option)}
              optionPathname="name"
            />
          </div>

          <div className={classes.btns_wrapper}>
            <Button
              title="Clear"
              clearButton
              onClick={clearFilters}
            />
          </div>
        </div>

        <div
          className={classes.dashboard_wrapper}
        >
          <div className={classes.dashboard_wrapper_filters}>
            <div className={classes.filter_wrapper}>
              <FilterButton
                activeValue={chartType}
                buttonsData={chartTypes}
                onClick={changeType}
                height={40}
                fontWeight={600}
                disableActiveButton
              />
            </div>
          </div>

          <div className={classes.chart_wrapper}>
            {!loading && !chartLoading
              ? (chart.series?.[0]?.data?.length
                ? <Chart options={chart.options} series={chart.series} type="bar" width="100%" height={270} />
                : <div className={classes.chart_is_empty}>No data to display</div>)
              : <Loader size={40} className={classes.chart_wrapper_loader} />}
          </div>

        </div>
      </div>

      <div className={classes.users_wrapper}>
        <InputField
          value={searchValue}
          placeholder="Physician"
          onChange={setSearchValue}
          clearValue
        />

        <div className={classes.physicians}>
          {loading
            ? <Loader size={40} />

            : !filteredPhysicians.length
              ? <div className={classes.physician_not_found}>Physician not found</div>

              : filteredPhysicians.map(({
                id, fullName, active, color,
              }) => (
                <div key={id} className={classes.physician_wrapper}>
                  <button
                    className={`${classes.physician} 
                  ${(disabled && !active) || (active && activePhysiciansCount === 1)
                      ? classes.disabled

                      : active
                        ? classes.active

                        : ''}`}
                    onClick={() => (
                      ((active && activePhysiciansCount > 1) || (!disabled && !active))
                    && changePhysiciansData(id, !active)
                    )}
                    style={(active && activePhysiciansCount === 1) ? { textDecoration: 'none' } : {}}
                    type="button"
                  >

                    <span style={{ background: color }} />

                    <span title={fullName}>{fullName}</span>
                  </button>

                  <div className={classes.view_all}>
                    <NavLink to={`/dashboard/physicians-report${queryToStringify({
                      physicianId: id,
                      physicianName: fullName,
                      startDate: filters.start || filters.end,
                      dateType: 'single',
                    })}`}
                    >
                      view
                    </NavLink>
                  </div>
                </div>
              ))}
        </div>
      </div>
    </div>
  );
};

export default TopPhysiciansByRevenue;
